
import { useState, useEffect, useCallback, useRef } from 'react';

export interface VideoStats {
  viewCount: number;
  totalWatchTime: number; // in seconds
  lastWatched: string | null;
  watchHistory: WatchSession[];
}

export interface WatchSession {
  timestamp: string;
  duration: number; // seconds watched in this session
}

interface AllVideoStats {
  [videoId: string]: VideoStats;
}

const STORAGE_KEY = 'catflix_video_stats';
const VIEW_THRESHOLD = 5; // seconds before counting as a view

// Load stats from localStorage
const loadStats = (): AllVideoStats => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

// Save stats to localStorage
const saveStats = (stats: AllVideoStats): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stats));
  } catch {
    console.error('Failed to save video stats');
  }
};

// Format view count for display
export const formatViewCount = (count: number): string => {
  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1)}M`;
  }
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}K`;
  }
  return count.toString();
};

// Format watch time for display
export const formatWatchTime = (seconds: number): string => {
  if (seconds < 60) {
    return `${Math.floor(seconds)}s`;
  }
  if (seconds < 3600) {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  }
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
};

export function useVideoStats(videoId: string) {
  const [stats, setStats] = useState<VideoStats>({
    viewCount: 0,
    totalWatchTime: 0,
    lastWatched: null,
    watchHistory: [],
  });
  const [currentSessionTime, setCurrentSessionTime] = useState(0);
  const [isTracking, setIsTracking] = useState(false);
  const [hasCountedView, setHasCountedView] = useState(false);
  
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number | null>(null);

  // Load initial stats for this video
  useEffect(() => {
    const allStats = loadStats();
    const videoStats = allStats[videoId] || {
      viewCount: Math.floor(Math.random() * 5000) + 100, // Initial random views for demo
      totalWatchTime: Math.floor(Math.random() * 10000),
      lastWatched: null,
      watchHistory: [],
    };
    setStats(videoStats);
    setCurrentSessionTime(0);
    setHasCountedView(false);
  }, [videoId]);

  // Start tracking watch time
  const startTracking = useCallback(() => {
    if (isTracking) return;
    
    setIsTracking(true);
    startTimeRef.current = Date.now();
    
    intervalRef.current = setInterval(() => {
      if (startTimeRef.current) {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setCurrentSessionTime(elapsed);
        
        // Count as a view after threshold
        if (elapsed >= VIEW_THRESHOLD && !hasCountedView) {
          setHasCountedView(true);
          setStats(prev => {
            const updated = {
              ...prev,
              viewCount: prev.viewCount + 1,
            };
            // Save immediately when view is counted
            const allStats = loadStats();
            allStats[videoId] = updated;
            saveStats(allStats);
            return updated;
          });
        }
      }
    }, 1000);
  }, [isTracking, hasCountedView, videoId]);

  // Stop tracking and save session
  const stopTracking = useCallback(() => {
    if (!isTracking) return;
    
    setIsTracking(false);
    
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    if (currentSessionTime > 0) {
      const session: WatchSession = {
        timestamp: new Date().toISOString(),
        duration: currentSessionTime,
      };
      
      setStats(prev => {
        const updated = {
          ...prev,
          totalWatchTime: prev.totalWatchTime + currentSessionTime,
          lastWatched: new Date().toISOString(),
          watchHistory: [...prev.watchHistory.slice(-9), session], // Keep last 10 sessions
        };
        
        // Save to localStorage
        const allStats = loadStats();
        allStats[videoId] = updated;
        saveStats(allStats);
        
        return updated;
      });
    }
    
    startTimeRef.current = null;
  }, [isTracking, currentSessionTime, videoId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return {
    stats,
    currentSessionTime,
    isTracking,
    startTracking,
    stopTracking,
    formatViewCount,
    formatWatchTime,
  };
}

// Hook to get global stats across all videos
export function useGlobalVideoStats() {
  const [globalStats, setGlobalStats] = useState({
    totalViews: 0,
    totalWatchTime: 0,
    videosWatched: 0,
  });

  useEffect(() => {
    const allStats = loadStats();
    const videos = Object.values(allStats);
    
    setGlobalStats({
      totalViews: videos.reduce((sum, v) => sum + v.viewCount, 0),
      totalWatchTime: videos.reduce((sum, v) => sum + v.totalWatchTime, 0),
      videosWatched: videos.length,
    });
  }, []);

  return globalStats;
}
